/* eslint-disable no-underscore-dangle */
import React, { useEffect, useRef, useState } from 'react';
import AtaRegistrationPriceItem from 'components/molecules/ata_registration_price_item';
import ItemTools from 'components/molecules/item_tools';
import { ApplicationState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { AtasState, AtaTypes } from 'store/modules/ata_registration_price_store/types';
import SelectButton from 'components/atoms/select_button';
import ItemShare from 'components/atoms/item_share';
import { getDownloadAtas } from 'services/actions/bidding_actions';
import EmptyResult from 'components/atoms/empty_result';
import Pagination from 'components/atoms/pagination';
import Loader from 'components/atoms/loader';
import { useReactToPrint } from 'react-to-print';
import { hasCookie, setCookie } from 'services/actions/cookies_actions';
import OpenTutorial from 'components/atoms/open_tutorial';
import { Container } from './styles';

interface AtaRegistrationPriceListProps {
  setPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
}

const AtaRegistrationPriceList = (props: AtaRegistrationPriceListProps) => {
  const dispatch = useDispatch();
  const { setPage, page } = props;
  const componentRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const [selectedAtas, setSelectedAtas] = useState<Array<number>>([]);
  const [showShareWindow, setShowShareWindow] = useState<boolean>(false);
  const [modalVideoOpened, setModalVideoOpened] = useState<boolean>(true);
  const [scrollToolsClass, setScrollToolsClass] = useState<string>('');
  const [wholePageSelected, setWholePageSelected] = useState<boolean>(false);

  const { atas, request } = useSelector<ApplicationState, AtasState>((state) => state.session.atas);

  useEffect(() => {
    setModalVideoOpened(!hasCookie('modalAtas'));
  }, []);

  useEffect(() => {
    const windowDiv = document.getElementById('windowDiv');
    windowDiv?.addEventListener('scroll', () => {
      setScrollToolsClass(windowDiv?.scrollTop > 10 ? 'border-bottom' : '');
    });
  }, []);

  useEffect(() => {
    if (modalVideoOpened) setCookie('modalAtas', true);
  }, [modalVideoOpened]);

  const toggleSelectedAtas = (id: number, updateAll = false) => {
    if (updateAll) {
      if (wholePageSelected) {
        const atasList = atas?.ata?.map((ata) => ata?._source?.id);
        setSelectedAtas(selectedAtas.filter((selectedAtas) => !atasList.includes(selectedAtas)));
      }
      if (!wholePageSelected)
        setSelectedAtas([...selectedAtas, ...(atas?.ata?.map((ata) => ata?._source?.id) || [])]);
    }
    if (!updateAll) {
      if (selectedAtas.includes(id))
        setSelectedAtas(selectedAtas.filter((selectedAtas) => selectedAtas !== id));
      if (!selectedAtas.includes(id)) setSelectedAtas([...selectedAtas, id]);
    }
  };

  const downloadFile = (fileExtension: string) => {
    getDownloadAtas(fileExtension, 'Atas', selectedAtas, selectedAtas.length, false);
  };

  const handleShare = () => {
    setShowShareWindow(!showShareWindow);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'conlicitacao',
  });

  const updateCurrentPage = (currentPage = 1) => {
    dispatch({ type: AtaTypes.UPDATE_CURRENT_PAGE, currentPage });
    setPage(currentPage);
  };

  useEffect(() => {
    if (!atas?.ata) return;

    setWholePageSelected(
      atas?.ata?.map((ata) => ata?._source?.id).filter((ata) => selectedAtas.includes(ata))
        ?.length === atas?.ata?.length,
    );
  }, [selectedAtas, atas.currentPage, atas.postsPerPage, atas?.ata]);

  return (
    <Container className="d-print-none">
      <div className="row">
        <div className="col-12 col-lg-11 mx-auto mt-4">
          <div className="d-md-flex d-block align-items-center">
            <h4 className="mb-md-0 mb-1 mr-3">Ata de Registro de Preço</h4>
            <OpenTutorial type="modal" source="ata" />
          </div>
          <div className={`row list-tools py-1 ${scrollToolsClass}`}>
            <div className="position-relative col-md-6 d-block py-3 px-2">
              <SelectButton
                isChecked={wholePageSelected}
                id={0}
                toggleSelected={toggleSelectedAtas}
                text="Selecionar página"
              />
              {selectedAtas.length > 0 && (
                <div className="itemToolsSelected ml-3">
                  {selectedAtas.length}{' '}
                  {selectedAtas.length === 1 ? 'registro selecionado.' : 'registros selecionados.'}
                  <button
                    type="button"
                    className="btn btn-link m-0 py-0 px-1 border-0"
                    onClick={() => setSelectedAtas([])}
                  >
                    Limpar seleção.
                  </button>
                </div>
              )}
            </div>
            <ItemTools
              enabledButtons={selectedAtas.length > 0}
              disabledPopoverLabel="a ata"
              downloadFile={downloadFile}
              handleShare={handleShare}
              handlePrint={handlePrint}
            />
            {showShareWindow && (
              <ItemShare setShowShareWindow={setShowShareWindow} selectedItems={selectedAtas} />
            )}
          </div>
          {request.loading && (
            <div className="d-flex align-items-center justify-content-center loading-container">
              <Loader />
            </div>
          )}
          <div ref={componentRef} className="row mt-3">
            <div className="col-12">
              {request.loaded &&
                atas?.ata?.map((ata, index) => (
                  <AtaRegistrationPriceItem
                    key={ata?._source?.id}
                    ata={ata}
                    AtaIndex={index + (page - 1) * 20}
                    toggleSelectedAtas={toggleSelectedAtas}
                    isChecked={selectedAtas.includes(ata?._source?.id)}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
      {request.loaded && atas?.ata?.length === 0 && (
        <EmptyResult
          title="Desculpe!"
          description="Não temos contratos que correspondam a esses filtros."
        />
      )}
      {request.loaded && atas.total_entries !== 0 && (
        <div className="row d-print-none">
          <div className="col-md-12 col-lg-11 mx-auto mb-5 pb-5">
            <Pagination
              postsPerPage={atas?.postsPerPage}
              totalPosts={atas?.total_entries}
              paginate={updateCurrentPage}
              currentPage={page}
              firstAndLastPage
            />
          </div>
        </div>
      )}
    </Container>
  );
};

export default AtaRegistrationPriceList;
